import { useRef, useState, useCallback } from "react";

/**
 * Minimal HTML5 drag-and-drop reorder hook (no extra deps).
 *
 * Usage:
 *   const { getDragProps, draggedId } = useDragReorder(items, onReorder);
 *   items.map(item => <li {...getDragProps(item.id)}>...</li>)
 *
 * onReorder receives the new array of IDs in display order.
 */
export function useDragReorder(items, onReorder) {
  const draggedRef = useRef(null);
  const [draggedId, setDraggedId] = useState(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = useCallback((id) => (e) => {
    draggedRef.current = id;
    setDraggedId(id);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
    } catch {
      // ignore safari hiccups
    }
  }, []);

  const onDragOver = useCallback((id) => (e) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  }, [overId]);

  const onDragEnd = useCallback(() => {
    draggedRef.current = null;
    setDraggedId(null);
    setOverId(null);
  }, []);

  const onDrop = useCallback((targetId) => (e) => {
    e.preventDefault();
    const fromId = draggedRef.current;
    draggedRef.current = null;
    setDraggedId(null);
    setOverId(null);
    if (!fromId || fromId === targetId) return;
    const ids = items.map((it) => it.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    onReorder(next);
  }, [items, onReorder]);

  const getDragProps = useCallback((id) => ({
    draggable: true,
    onDragStart: onDragStart(id),
    onDragOver: onDragOver(id),
    onDragEnd: onDragEnd,
    onDrop: onDrop(id),
    "data-dragging": draggedId === id ? "true" : undefined,
    "data-drag-over": overId === id && draggedId !== id ? "true" : undefined,
  }), [onDragStart, onDragOver, onDragEnd, onDrop, draggedId, overId]);

  return { getDragProps, draggedId, overId };
}
